import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { Dropdown } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp as faChevronUpSolid } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { faChevronDown as faChevronDownSolid } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp as faChevronUpLight } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { faChevronDown as faChevronDownLight } from '@fortawesome/pro-light-svg-icons/faChevronDown';

import { alphaHeaders, SEO_PREMIUM_PUBLICATIONS_BREADCRUMBS } from '../../../data/directory/constants';
import Breadcrumbs from '../../Breadcrumbs';

import useSizes from '../../../hooks/sizes';

import Styles from './styles.module.scss';
import PremiumPublicationSearchFilter from './PremiumPublicationsSearchFilter';
import PremiumPublicationsCard from './PremiumPublicationsCard';
import { publicationTabs } from '../../../data/directory/publications/publications';
import { getDomains } from '../../../services/dbServices/storyFiltersDbServices';
import Pagination from './Pagination';
import AlphaNumericPagination from './Pagination/AlphaNumericPagination';
import PremiumPublicationNews from './PremiumPublicationsNews';
import Tabs from '../../WatchlistComponents/WatchlistPageWrapper/Tabs';

const cx = classNames.bind(Styles);

let timer = 0;
const PremiumPublications = () => {
  const descRef = useRef();
  const firstUpdate = useRef(true);
  const [hasShowMore, setHasShowMore] = useState(false);
  const [isShowMore, setIsShowMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [publicationLists, setPublicationsList] = useState([]);
  const [activeTab, setActiveTab] = useState(1);
  const [activeAlphaHeader, setActiveAlphaHeader] = useState('All');
  const [perPageValue, setPerPageValue] = useState(15);
  const [totalPageCount, setTotalPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const { width } = useSizes();
  const searchBarActive = useSelector(({ searchReducers }) => (
    searchReducers.searchBarActive
  ));

  const toggleDropdown = () => {
    setFilterOpen((filterOpen) => !filterOpen);
  };

  const handleTabClick = async (id) => {
    setActiveTab(id);
    setCurrentPage(1);
  };

  const fetchPublicationsList = async (params) => {
    const updatedParams = {
      is_premium: true,
      ...params,
    };
    if (activeTab === 2) {
      updatedParams.major = true;
    }
    if (searchQuery) { updatedParams.query = searchQuery; }
    return getDomains(updatedParams);
  };

  const handleAlphaHeaderTabClick = async (id) => {
    const params = {};
    setCurrentPage(1);
    params.page = currentPage;
    params.per_page = perPageValue;
    if ((id !== activeAlphaHeader)
      && (id.toLowerCase() !== 'all')) {
      setActiveAlphaHeader(id);
      params.prefix = id.toLowerCase();
      fetchPublicationsList(params)
        .then(({ data, headers: { total: totalAll } }) => {
          setPublicationsList(data);
          setTotalPageCount(totalAll || 1);
        })
        .catch(() => { });
    } else if (id.toLowerCase() === 'all') {
      params.per_page = perPageValue;
      setActiveAlphaHeader(id);
      fetchPublicationsList(params)
        .then(({ data, headers: { total: totalAll } }) => {
          setPublicationsList(data);
          setTotalPageCount(totalAll || 1);
        })
        .catch(() => { });
    }
  };

  const handleAlphaHeaderChange = (value) => {
    handleAlphaHeaderTabClick(value);
  };

  const handleSearch = async (e) => {
    clearTimeout(timer);
    e.persist();
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleClearSearch = async () => {
    clearTimeout(timer);
    setSearchQuery('');
    setCurrentPage(1);
  };

  const onOptionSelected = (value) => {
    setPerPageValue(value);
    setCurrentPage(1);
  };

  const onPageChange = (value) => {
    setCurrentPage(value);
  };

  useEffect(() => {
    const params = {};
    setLoading(true);
    params.page = currentPage;
    params.per_page = perPageValue;
    if (activeAlphaHeader && activeAlphaHeader.toLowerCase() !== 'all') {
      params.prefix = activeAlphaHeader.toLowerCase();
      fetchPublicationsList(params)
        .then(({ data, headers: { total: totalAll } }) => {
          setPublicationsList(data);
          setTotalPageCount(totalAll || 1);
          setLoading(false);
        })
        .catch(() => { setLoading(false); });
      return;
    }
    fetchPublicationsList(params)
      .then(({ data, headers: { total: totalAll } }) => {
        setPublicationsList(data);
        setTotalPageCount(totalAll || 1);
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, [activeTab, currentPage, perPageValue]);

  useEffect(() => {
    if (!searchQuery && firstUpdate.current) return;
    setLoading(true);
    const params = {
      page: currentPage,
      per_page: perPageValue,
    };
    if (activeAlphaHeader && activeAlphaHeader.toLowerCase() !== 'all') {
      params.prefix = activeAlphaHeader.toLowerCase();
    }
    timer = setTimeout(async () => {
      fetchPublicationsList(params)
        .then(({ data, headers: { total: totalAll } }) => {
          setPublicationsList(data);
          setTotalPageCount(totalAll || 1);
          setLoading(false);
        })
        .catch(() => { });
    }, 300);
  }, [searchQuery]);

  useEffect(() => {
    if (width > 1024) {
      setPerPageValue(15);
    } else if (width > 767) {
      setPerPageValue(10);
    } else if (width < 768) {
      setPerPageValue(5);
    }
    if (width < 400 && descRef.current) {
      return setHasShowMore(descRef.current.scrollHeight > 70);
    }
    setHasShowMore(false);
  }, [width]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
    }
  }, []);

  const toggleExpand = () => {
    setIsShowMore(!isShowMore);
  };

  return (
    <div className={cx('premiumPublications', searchBarActive && 'blurred')}>
      <div className={cx('premiumPublications__header')}>
        <Breadcrumbs items={SEO_PREMIUM_PUBLICATIONS_BREADCRUMBS} />
        <div className={cx('header_title')}>
          Premium Publications
        </div>
        <div className={cx('header_desc', { expand: isShowMore, show_more: hasShowMore })} ref={descRef}>
          There are billions of websites out there but only a few pass the Content
          Quality Test. Here you can browse top publications and select
          content for business, political,
          and financial news. No paywalls, no advertising, no hassle.
        </div>
        {hasShowMore && (
          <div className={Styles.description_show} onClick={toggleExpand}>
            Show&nbsp;
            {isShowMore ? 'Less' : 'More'}
            <FontAwesomeIcon className="img mob" icon={isShowMore ? faChevronUpSolid : faChevronDownSolid} />
          </div>
        )}
      </div>
      <div className={cx('premiumPublication__container')}>
        <section className={cx('publicationSection')}>
          <div className={cx('publicationTitle')}>
            Publications
          </div>
          <div className={cx('publicationFilterContainer')}>
            <PremiumPublicationSearchFilter
              searchQuery={searchQuery}
              handleSearch={handleSearch}
              handleClearSearch={handleClearSearch}
            />
            <AlphaNumericPagination
              tabs={alphaHeaders}
              activeTab={activeAlphaHeader}
              onTabClick={handleAlphaHeaderTabClick}
            />
            <div className={Styles['letters-select']}>
              <Dropdown
                id={cx('alphaHeader_dropdown--container')}
                onToggle={toggleDropdown}
                show={filterOpen}
              >
                <Dropdown.Toggle>
                  <div className={cx('alphaHeader_dropdown--title')}>
                    {activeAlphaHeader}
                  </div>
                  <div className={cx('alphaHeader_dropdown--arrow')}>
                    <FontAwesomeIcon className="img mob" icon={filterOpen ? faChevronUpLight : faChevronDownLight} />
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu
                  rootCloseEvent="click"
                  flip={false}
                  show={filterOpen}
                >
                  {
                    alphaHeaders?.map((letter, index) => (
                      <Options
                        key={letter}
                        label={letter}
                        value={letter}
                        selectedValue={activeAlphaHeader}
                        onSelect={handleAlphaHeaderChange}
                        filterOpen={filterOpen}
                        index={index}
                      />
                    ))
                  }
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
          <Tabs
            tabs={publicationTabs}
            activeTab={activeTab}
            onTabClick={handleTabClick}
            classname="publicationTabs"
          />
          <PremiumPublicationsCard
            publicationLists={publicationLists}
            highlight={searchQuery}
            loading={loading}
          />
          {publicationLists.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalCount={totalPageCount}
              setCurrentPage={onPageChange}
              pageSize={perPageValue}
              setSelectedValue={onOptionSelected}
              className="pagination-bar"
              publicationCount={publicationLists.length}
            />
          )}
        </section>
        <section className={cx('publicationNews')}>
          <PremiumPublicationNews />
        </section>
      </div>
    </div>
  );
};

const Options = ({
  label, value, selectedValue, onSelect, index, filterOpen,
}) => (
  <Dropdown.Item
    eventKey={value}
    active={value === selectedValue}
    onSelect={onSelect}
  >
    <div className={cx('option--title')}>
      {label}
    </div>
    {(index === 0 && filterOpen) && (
      <div className={cx('option--arrow')}>
        <FontAwesomeIcon icon={faChevronUpLight} />
      </div>
    )}
  </Dropdown.Item>
);

export default PremiumPublications;
